import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	Chip,
	Container,
	Divider,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	LinearProgress,
	Paper,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import { BarChart } from '@mui/x-charts/BarChart';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import SvgColor from '../components/svg-color';

const token =
	'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJldmVudGxpc3QiOlt7IlVzZXJJRCI6IjEiLCJMb2dpbkNvZGUiOiIwMSIsIkxvZ2luTmFtZSI6IkFkbWluIiwiRW1haWxJZCI6ImFkbWluQGdtYWlsLmNvbSIsIlVzZXJUeXBlIjoiQURNSU4ifV0sImlhdCI6MTYzODM1NDczMX0.ZW6zEHIXTxfT-QWEzS6-GuY7bRupf2Jc_tp4fXIRabQ';

const numberFormatter = new Intl.NumberFormat('en-IN');
const currencyFormatter = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatNumberOrEmpty = (value) => {
	const numericValue = Number(value || 0);
	if (numericValue === 0) return '';
	return numberFormatter.format(numericValue);
};
const formatCurrencyOrEmpty = (value) => {
	const numericValue = Number(value || 0);
	if (numericValue === 0) return '';
	return currencyFormatter.format(numericValue);
};

const getCostPerPiece = (value, quantity) => {
	const numericValue = Number(value || 0);
	const numericQty = Number(quantity || 0);
	if (!numericQty) return 0;
	return numericValue / numericQty;
};

const getNumericValue = (row, keys) => {
	for (let index = 0; index < keys.length; index += 1) {
		const key = keys[index];
		const value = Number(row?.[key] ?? 0);
		if (!Number.isNaN(value)) {
			return value;
		}
	}
	return 0;
};

const normalizeDashboardData = (rows = []) =>
	rows.map((row, index) => ({
		id: row.ProcessId ?? index + 1,
		processId: row.ProcessId ?? index + 1,
		processName: row.DepartmentDescription || `Process ${index + 1}`,
		departmentName: row.DepartmentDescription || '-',
		currentDateQty: getNumericValue(row, ['CurrentDateQty', 'CurrentPassedDateQty', 'CurrentQty', 'ProcessQty']),
		monthToDateQty: getNumericValue(row, ['MonthToDateQty', 'MonthQty', 'MTDQty', 'ProcessQty']),
		yearToDateQty: getNumericValue(row, ['YearToDateQty', 'YearQty', 'YTDQty', 'ProcessQty']),
		currentDateValue: getNumericValue(row, ['CurrentDateValue', 'CurrentValue', 'TodayValue', 'ProcessValue']),
		monthToDateValue: getNumericValue(row, ['MonthToDateValue', 'MonthValue', 'MTDValue', 'ProcessValue']),
		yearToDateValue: getNumericValue(row, ['YearToDateValue', 'YearValue', 'YTDValue', 'ProcessValue']),
	}));

const buildDashboardPayload = (toDate, obProduction) => {
	const companyId = sessionStorage.getItem('CompanyId');
	const factoryId = sessionStorage.getItem('factoryID');

	const payload = {
		ToDate: toDate,
	};

	if (companyId && companyId !== '0') {
		payload.companyId = Number(companyId);
	}

	if (factoryId && factoryId !== '0') {
		payload.factoryId = Number(factoryId);
	}

	if (obProduction !== undefined && obProduction !== null) {
		payload.IsOBProduction = obProduction ? 'Y' : 'N';
	}

	return payload;
};

const PROCESS_COLORS = ['#d94f30', '#1f7a8c', '#2d5b27', '#7c3f00', '#5c4a8f', '#b5451b', '#0a7075'];
const PROCESS_TABLE_COLUMN_COLORS = {
	current: alpha('#d94f30', 0.08),
	month: '#eaf4ff',
	year: '#edf8d6',
};

const MetricCard = ({ accent, icon, label, value, helper }) => (
	<Card
		sx={{
			p: 1.5,
			height: '100%',
			borderRadius: 2.5,
			color: '#132238',
			background: `linear-gradient(135deg, ${alpha(accent, 0.18)} 0%, #ffffff 58%, ${alpha(accent, 0.08)} 100%)`,
			border: `1px solid ${alpha(accent, 0.18)}`,
			boxShadow: '0 10px 24px rgba(16, 24, 40, 0.08)',
		}}
	>
		<Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
			<Box>
				<Typography sx={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase', color: '#5b6b79' }}>
					{label}
				</Typography>
				<Typography sx={{ mt: 1, fontSize: 24, fontWeight: 800, fontFamily: 'Trebuchet MS, Segoe UI, sans-serif', lineHeight: 1.1 }}>
					{numberFormatter.format(value)}
				</Typography>
				<Typography sx={{ mt: 0.5, color: '#60707d', fontSize: 12 }}>{helper}</Typography>
			</Box>
			<Box
				sx={{
					width: 42,
					height: 42,
					borderRadius: 2,
					display: 'grid',
					placeItems: 'center',
					color: accent,
					backgroundColor: alpha(accent, 0.15),
				}}
			>
				{icon}
			</Box>
		</Stack>
	</Card>
);

export default function DashboardAppPage() {
	const navigate = useNavigate();
	const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
	const [dashboardRows, setDashboardRows] = useState([]);
	const [monthlyExpenseBreakdown, setMonthlyExpenseBreakdown] = useState([]);
	const [yearlyExpenseBreakdown, setYearlyExpenseBreakdown] = useState([]);
	const [monthwiseProductionCostReport, setMonthwiseProductionCostReport] = useState([]);
	const [expenseBreakdownPopupOpen, setExpenseBreakdownPopupOpen] = useState(false);
	const [yearExpenseBreakdownPopupOpen, setYearExpenseBreakdownPopupOpen] = useState(false);
	const [expenseSummary, setExpenseSummary] = useState({
		selectedMonthTotal: 0,
		selectedYearTotal: 0,
		selectedMonthLabel: moment().format('MMMM'),
		selectedYear: moment().format('YYYY'),
	});
	const [openLoader, setOpenLoader] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [infoMessage, setInfoMessage] = useState('');
  const [obProduction, setObProduction] = useState(true);

	const getExpenseTotal = (rows = []) =>
		rows.reduce((total, item) => total + Number(item?.AmountPerMonth || 0), 0);

	const yearlyMonthSummary = useMemo(() => {
		const groupedByMonth = {};

		yearlyExpenseBreakdown.forEach((item) => {
			const monthNumber = Number(item?.Month || 0);
			if (!monthNumber) return;

			if (!groupedByMonth[monthNumber]) {
				groupedByMonth[monthNumber] = { month: monthNumber, total: 0, itemCount: 0 };
			}

			groupedByMonth[monthNumber].total += Number(item?.AmountPerMonth || 0);
			groupedByMonth[monthNumber].itemCount += 1;
		});

		return Object.values(groupedByMonth).sort((a, b) => a.month - b.month);
	}, [yearlyExpenseBreakdown]);

	const fetchFactoryExpenseSummary = async (selectedDate) => {
		const parsedDate = moment(selectedDate, 'YYYY-MM-DD', true);
		const validDate = parsedDate.isValid() ? parsedDate : moment(selectedDate);
		const finalDate = validDate.isValid() ? validDate : moment();

		const selectedMonth = finalDate.month() + 1;
		const selectedYear = finalDate.year();
		const factoryUnit = sessionStorage.getItem('factoryID');
		const payloadBase = {
			YEAR: selectedYear,
			Factory_Unit: factoryUnit && factoryUnit !== '0' ? factoryUnit : 0,
		};

		try {
			const [selectedMonthRes, selectedYearRes] = await Promise.all([
				axios.instance.post(
					'/GetFilteredFactoryExpenseData',
					{ ...payloadBase, Month: selectedMonth },
					{ headers: { Authorization: token, 'Content-Type': 'application/json' } }
				),
				axios.instance.post(
					'/GetFilteredFactoryExpenseData',
					{ ...payloadBase, Month: 0 },
					{ headers: { Authorization: token, 'Content-Type': 'application/json' } }
				),
			]);

			const selectedMonthRows = Array.isArray(selectedMonthRes?.data) ? selectedMonthRes.data : [];
			const selectedYearRows = Array.isArray(selectedYearRes?.data) ? selectedYearRes.data : [];

			setMonthlyExpenseBreakdown(selectedMonthRows);
			setYearlyExpenseBreakdown(selectedYearRows);
			setExpenseSummary({
				selectedMonthTotal: getExpenseTotal(selectedMonthRows),
				selectedYearTotal: getExpenseTotal(selectedYearRows),
				selectedMonthLabel: finalDate.format('MMMM'),
				selectedYear: String(selectedYear),
			});
		} catch (error) {
			console.error('Failed to load factory expense summary:', error);
			setMonthlyExpenseBreakdown([]);
			setYearlyExpenseBreakdown([]);
			setExpenseSummary({
				selectedMonthTotal: 0,
				selectedYearTotal: 0,
				selectedMonthLabel: finalDate.format('MMMM'),
				selectedYear: String(selectedYear),
			});
		}
	};

	const fetchDashboardData = async (selectedDate = toDate) => {
		setOpenLoader(true);
		try {
			const response = await axios.instance.post('/GetDashboardReport', buildDashboardPayload(selectedDate, obProduction), {
				headers: { Authorization: token, 'Content-Type': 'application/json' },
			});

			const normalizedRows = normalizeDashboardData(Array.isArray(response.data) ? response.data : []);
			setDashboardRows(normalizedRows);

			if (normalizedRows.length === 0) {
				setInfoMessage('No dashboard data found for the selected date.');
			}

			await fetchFactoryExpenseSummary(selectedDate);
		} catch (error) {
			setErrorMessage(error?.response?.data?.message || 'Failed to load dashboard data.');
		} finally {
			setOpenLoader(false);
		}
	};

	useEffect(() => {
        GetOneControl();
		fetchDashboardData(toDate);
		GetMonthwiseProductionCostReport()
	}, []);

	const GetMonthwiseProductionCostReport = async (selectedDate = toDate) => {
		setOpenLoader(true);
		try {
			const parsed = moment(selectedDate, 'YYYY-MM-DD', true);
			const finalDate = parsed.isValid() ? parsed : moment(selectedDate);
			const payload = {
				YEAR: finalDate.year(),
				Factory_Unit: sessionStorage.getItem('factoryID'),
				IsOBProduction: obProduction ? 'Y' : 'N',
				
			};

			const res = await axios.instance.post('/GetMonthwiseProductionCostReport', payload, {
				headers: { Authorization: token, 'Content-Type': 'application/json' },
			});
			setMonthwiseProductionCostReport(Array.isArray(res.data) ? res.data : []);
		} catch (error) {
			console.error('Error fetching monthwise production cost report:', error);
			setMonthwiseProductionCostReport([]);
		} finally {
			setOpenLoader(false);
		}
	};

   const GetOneControl = async () => {
    setOpenLoader(true);
    try {
      const res = await axios.instance.get(
        `/GetOneControlCenter/${sessionStorage.getItem('factoryID')}/${sessionStorage.getItem('CompanyId')}`,
        {
          headers: { Authorization: token, 'Content-Type': 'application/json' },
        }
      );
      if (res.data.length > 0) {
        res.data.forEach((data) => {
          setObProduction(data.OBProduction === 'Y');
        });
      } else {
        setObProduction(false);
      }
      setOpenLoader(false);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

	const summary = useMemo(() => {
		const totals = dashboardRows.reduce(
			(accumulator, row) => ({
				currentDateQty: accumulator.currentDateQty + row.currentDateQty,
				monthToDateQty: accumulator.monthToDateQty + row.monthToDateQty,
				yearToDateQty: accumulator.yearToDateQty + row.yearToDateQty,
        YearToDateValue: accumulator.YearToDateValue + row.yearToDateValue,
			}),
			{ currentDateQty: 0, monthToDateQty: 0, yearToDateQty: 0 }
		);

		const finishingRow = dashboardRows.find((r) =>
			r.processName.toLowerCase().includes('finishing')
		);

    const packingRow = dashboardRows.find((r) =>
			r.processName.toLowerCase().includes('packing')
		);

		return {
			...totals,
			totalProcesses: dashboardRows.length,
			finishingQty: finishingRow ? finishingRow.yearToDateQty : 0,
      packingQty: packingRow ? packingRow.yearToDateQty : 0,
		};
	}, [dashboardRows]);

	const monthwiseCostByMonth = useMemo(() => {
		const source = Array.isArray(monthwiseProductionCostReport) ? monthwiseProductionCostReport : [];
		const monthMap = new Map(source.map((row) => [Number(row?.MonthNumber), row]));

		return Array.from({ length: 12 }, (_, index) => {
			const monthNumber = index + 1;
			const existing = monthMap.get(monthNumber);
			return (
				existing || {
					MonthNumber: monthNumber,
					MonthName: moment().month(index).format('MMM'),
					Year: moment(toDate).year(),
					Quantity: 0,
					Value: 0,
					Expense: 0,
					CPP: 0,
				}
			);
		});
	}, [monthwiseProductionCostReport, toDate]);

	const monthwiseCostTotals = useMemo(
		() =>
			monthwiseCostByMonth.reduce(
				(acc, row) => ({
					quantity: acc.quantity + Number(row?.Quantity || 0),
					value: acc.value + Number(row?.Value || 0),
					expense: acc.expense + Number(row?.Expense || 0),
				}),
				{ quantity: 0, value: 0, expense: 0 }
			),
		[monthwiseCostByMonth]
	);

	const handleRefresh = () => {
		fetchDashboardData(toDate);
		GetMonthwiseProductionCostReport(toDate);
	};

	const handleOpenExpenseBreakdownPopup = () => {
		if (monthlyExpenseBreakdown.length > 0) {
			setExpenseBreakdownPopupOpen(true);
		}
	};

	const handleOpenYearExpenseBreakdownPopup = () => {
		if (yearlyExpenseBreakdown.length > 0) {
			setYearExpenseBreakdownPopupOpen(true);
		}
	};

	const handleProcessTrackerClick = (processId = 0, processName = 'All') => {
		navigate('/dashboard/reports/process_tracker', {
			state: {
				dashboardDate: toDate,
				autoApplyProcessTracker: true,
				processId,
				processName,
			},
		});
	};

	return (
		<Container maxWidth="xl" sx={{ py: 2 }}>
			<Box
				sx={{
					p: { xs: 1.75, md: 2.5 },
					borderRadius: 3,
					position: 'relative',
					overflow: 'hidden',
					background:
						'radial-gradient(circle at top left, rgba(255, 201, 107, 0.55), transparent 34%), linear-gradient(135deg, #10324d 0%, #184e77 48%, #f6bd60 120%)',
					color: '#fffdf7',
					boxShadow: '0 14px 34px rgba(16, 50, 77, 0.24)',
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						right: -60,
						top: -40,
						width: 220,
						height: 220,
						borderRadius: '50%',
						background: 'rgba(255,255,255,0.09)',
					}}
				/>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} md={7}>
						<Chip
							icon={<InsightsRoundedIcon />}
							label="Process Dashboard"
							sx={{
								mb: 1,
								px: 1,
								color: '#fff',
								backgroundColor: 'rgba(255,255,255,0.14)',
								'& .MuiChip-icon': { color: '#fff' },
							}}
						/>
						<Typography sx={{ mb: 0.4, color: 'rgba(255,255,255,0.72)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>
							Overall Finishing Qty (Year To Date)
						</Typography>
						<Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ gap: 1, flexWrap: 'wrap' }}>
							<Typography
								sx={{
									mt: 0.25,
									fontSize: { xs: 40, md: 54 },
									fontWeight: 900,
									lineHeight: 1,
									fontFamily: 'Trebuchet MS, Segoe UI, sans-serif',
									color: '#f6bd60',
								}}
							>
								{/* {numberFormatter.format(summary.finishingQty)} */}
              {numberFormatter.format(summary.packingQty)}
							</Typography>
							<Button
								variant="contained"
								onClick={() => handleProcessTrackerClick(0, 'All')}
								startIcon={
									<SvgColor
										src={`${process.env.PUBLIC_URL}/assets/icons/navbar/processTracker.svg`}
										sx={{ width: 16, height: 16 }}
									/>
								}
								size="small"
								sx={{
									mb: 0.6,
									px: 1.9,
									py: 0.45,
									minHeight: 34,
									fontWeight: 700,
									fontSize: 11,
									textTransform: 'none',
									borderRadius: 2,
									letterSpacing: 0.25,
									color: '#eaffff',
									background: 'linear-gradient(135deg, #0f6f78 0%, #1899a6 100%)',
									boxShadow: '0 8px 16px rgba(6, 37, 42, 0.3)',
									border: '1px solid rgba(130, 231, 241, 0.45)',
									'&:hover': {
										background: 'linear-gradient(135deg, #168996 0%, #20aebe 100%)',
										boxShadow: '0 10px 20px rgba(6, 37, 42, 0.36)',
										border: '1px solid rgba(169, 245, 255, 0.65)',
									},
								}}
							>
								Process Tracker
							</Button>
						</Stack>
						<Typography sx={{ mt: 0.5, color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
							{sessionStorage.getItem('Company_Name') || ''} {sessionStorage.getItem('factoryName') ? `· ${sessionStorage.getItem('factoryName')}` : ''}
						</Typography>
					</Grid>
					<Grid item xs={12} md={5}>
						<Paper
							sx={{
								p: 1.5,
								borderRadius: 2.5,
								backdropFilter: 'blur(12px)',
								backgroundColor: 'rgba(255,255,255,0.12)',
								border: '1px solid rgba(255,255,255,0.18)',
							}}
						>
							<Stack spacing={1.25}>
								<Stack direction="row" spacing={1.5} alignItems="center">
									<CalendarMonthRoundedIcon sx={{color:'white'}} />
									<Typography sx={{ fontWeight: 700, fontSize: 16 ,color: 'white'}}>Dashboard Filters</Typography>
								</Stack>
								<Box>
									<Typography sx={{ mb: 0.5, fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
										To Date
									</Typography>
									<TextField
										type="date"
										value={toDate}
										onChange={(event) => setToDate(event.target.value)}
										size="small"
										fullWidth
										sx={{
											'& .MuiInputBase-root': { backgroundColor: 'rgba(255,255,255,0.88)', borderRadius: 2 },
											'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
										}}
									/>
								</Box>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
									<Button
										variant="contained"
										onClick={handleRefresh}
										startIcon={<AutorenewRoundedIcon />}
										size="small"
										sx={{
											flex: 1,
											borderRadius: 2,
											backgroundColor: '#f6bd60',
											color: '#17324d',
											'&:hover': { backgroundColor: '#ffd58a' },
										}}
									>
										Refresh Dashboard
									</Button>
									<Button
										variant="outlined"
										onClick={() => {
											const today = moment().format('YYYY-MM-DD');
											setToDate(today);
											fetchDashboardData(today);
											GetMonthwiseProductionCostReport(today);
										}}
										size="small"
										sx={{
											flex: 1,
											borderRadius: 2,
											color: '#fff',
											borderColor: 'rgba(255,255,255,0.45)',
											'&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
										}}
									>
										Today
									</Button>
								</Stack>

							</Stack>
						</Paper>
					</Grid>
				</Grid>
			</Box>

			{openLoader && <LinearProgress sx={{ mt: 2, borderRadius: 999 }} />}

			{/* <Grid container spacing={3} sx={{ mt: 0.5 }}>
				<Grid item xs={12} sm={6} lg={3}>
					<MetricCard
						accent="#2d5b27"
						icon={<TimelineRoundedIcon />}
						label="Total Processes Qty"
						value={summary.yearToDateQty}
						helper={`${summary.totalProcesses} processes — year to date`}
					/>
				</Grid>
				<Grid item xs={12} sm={6} lg={3}>
					<MetricCard
						accent="#1f7a8c"
						icon={<InsightsRoundedIcon />}
						label="Total Finishing Qty"
						value={summary.finishingQty}
						helper="Finishing process — year to date"
					/>
				</Grid>
				<Grid item xs={12} sm={6} lg={3}>
					<MetricCard
						accent="#d94f30"
						icon={<BoltRoundedIcon />}
						label="Current Date Total"
						value={summary.currentDateQty}
						helper={`Qty on ${moment(toDate).format('DD MMM YYYY')}`}
					/>
				</Grid>
				<Grid item xs={12} sm={6} lg={3}>
					<MetricCard
						accent="#7c3f00"
						icon={<DateRangeRoundedIcon />}
						label="Month To Date Total"
						value={summary.monthToDateQty}
						helper={`From ${moment(toDate).startOf('month').format('DD MMM YYYY')}`}
					/>
				</Grid>
			</Grid> */}

			{dashboardRows.length > 0 ? (
				<Grid container spacing={1.75} sx={{ mt: 0.25 }}>
					{dashboardRows.map((row, index) => {
						const color = PROCESS_COLORS[index % PROCESS_COLORS.length];
						return (
							<Grid item xs={12} sm={6} lg={4} key={row.id}>
								<Paper
									sx={{
										p: 1.5,
										borderRadius: 3,
										border: `1px solid ${alpha(color, 0.2)}`,
										background: `linear-gradient(180deg, ${alpha(color, 0.06)} 0%, #ffffff 60%)`,
										boxShadow: '0 6px 18px rgba(16,24,40,0.06)',
									}}
								>
									<Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
										<Box>
											<Typography
												sx={{
													fontSize: 14,
													fontWeight: 800,
													color,
													cursor: 'pointer',
													display: 'inline-flex',
													alignItems: 'center',
													px: 1.15,
													py: 0.3,
													borderRadius: 1.5,
													backgroundColor: '#ffffff',
													border: `1px solid ${alpha(color, 0.34)}`,
													transition: 'all 0.2s ease',
													boxShadow: `0 2px 6px ${alpha(color, 0.12)}`,
													'&:hover': {
														backgroundColor: alpha(color, 0.1),
														color,
														border: `1px solid ${alpha(color, 0.5)}`,
														transform: 'translateY(-1px)',
														boxShadow: `0 6px 14px ${alpha(color, 0.2)}`,
													},
												}}
												onClick={() => handleProcessTrackerClick(row.processId, row.departmentName)}
											>
												{row.departmentName}
											</Typography>
											{/* <Typography sx={{ color: '#6b7a88', fontSize: 11 }}>{row.departmentName}</Typography> */}
										</Box>
										<Chip
											label={`YTD: ${numberFormatter.format(row.yearToDateQty)}`}
											size="small"
											sx={{
												fontWeight: 700,
												backgroundColor: alpha(color, 0.14),
												color,
											}}
										/>
									</Stack>
									<BarChart
										xAxis={[{ scaleType: 'band', data: ['Current', 'Month To Date', 'Year To Date'] }]}
										series={[{ data: [row.currentDateQty, row.monthToDateQty, row.yearToDateQty], color, label: row.processName }]}
										height={170}
										margin={{ top: 6, right: 8, left: 68, bottom: 36 }}
										slotProps={{ legend: { hidden: true } }}
									/>
									<Divider sx={{ my: 1 }} />
									<Stack direction="row" justifyContent="space-around">
										<Box sx={{ textAlign: 'center' }}>
											<Typography sx={{ fontSize: 10, color: '#5b6b79', textTransform: 'uppercase', letterSpacing: 0.5 }}>Current</Typography>
											<Typography sx={{ fontSize: 16, fontWeight: 800, color: '#d94f30' }}>{numberFormatter.format(row.currentDateQty)}</Typography>
										</Box>
										<Box sx={{ textAlign: 'center' }}>
											<Typography sx={{ fontSize: 10, color: '#5b6b79', textTransform: 'uppercase', letterSpacing: 0.5 }}>MTD</Typography>
											<Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1f7a8c' }}>{numberFormatter.format(row.monthToDateQty)}</Typography>
										</Box>
										<Box sx={{ textAlign: 'center' }}>
											<Typography sx={{ fontSize: 10, color: '#5b6b79', textTransform: 'uppercase', letterSpacing: 0.5 }}>YTD</Typography>
											<Typography sx={{ fontSize: 16, fontWeight: 800, color: '#2d5b27' }}>{numberFormatter.format(row.yearToDateQty)}</Typography>
										</Box>
									</Stack>
								</Paper>
							</Grid>
						);
					})}
				</Grid>
			) : (
				!openLoader && (
					<Box
						sx={{
							mt: 2,
							minHeight: 160,
							display: 'grid',
							placeItems: 'center',
							borderRadius: 3,
							border: '1px dashed rgba(16, 50, 77, 0.2)',
							backgroundColor: '#f8fbfd',
						}}
					>
						<Typography sx={{ color: '#637381' }}>No chart data available for the selected filters.</Typography>
					</Box>
				)
			)}

			<Paper
				sx={{
					mt: 2,
					borderRadius: 3,
					overflow: 'hidden',
					border: '1px solid rgba(16, 24, 40, 0.08)',
					boxShadow: '0 10px 24px rgba(16, 24, 40, 0.05)',
				}}
			>
				<Box
					sx={{
						px: 2,
						py: 1.25,
						background: 'linear-gradient(90deg, #f2f7fb 0%, #fff6e8 100%)',
					}}
				>
					<Typography sx={{ fontSize: 18, fontWeight: 800, color: '#12263a' }}>Process Wise Quantity And Value</Typography>
					<Typography sx={{ color: '#667784', fontSize: 12 }}>
						Each row shows process-wise quantity with the matching value for current date, month-to-date, and year-to-date.
					</Typography>
				</Box>
				<TableContainer sx={{ maxHeight: 440 }}>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell
									rowSpan={2}
									sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800, verticalAlign: 'middle' }}
								>
									Process
								</TableCell>
								<TableCell
									colSpan={3}
									align="center"
									sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}
								>
									Current Date
								</TableCell>
								<TableCell
									colSpan={3}
									align="center"
									sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}
								>
									Month To Date
								</TableCell>
								<TableCell
									colSpan={3}
									align="center"
									sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}
								>
									Year To Date
								</TableCell>
 							</TableRow>
							<TableRow>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									Qty
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									Value
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									CPP
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									Qty
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									Value
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									CPP
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									Qty
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									Value
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: '#10324d', color: '#fff', fontWeight: 800 }}>
									CPP
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{dashboardRows.length > 0 ? (
								dashboardRows.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell>
											<Typography sx={{ fontWeight: 700, color: '#132238' }}>{row.departmentName}</Typography>
										</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.current }}>{numberFormatter.format(row.currentDateQty)}</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.current }}>{numberFormatter.format(row.currentDateValue)}</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.current }}>
											{formatCurrencyOrEmpty(getCostPerPiece(row.currentDateValue, row.currentDateQty))}
										</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.month }}>{numberFormatter.format(row.monthToDateQty)}</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.month }}>{numberFormatter.format(row.monthToDateValue)}</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.month }}>
											{formatCurrencyOrEmpty(getCostPerPiece(row.monthToDateValue, row.monthToDateQty))}
										</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.year }}>
											<Typography sx={{ fontWeight: 800, color: '#2d5b27' }}>
												{numberFormatter.format(row.yearToDateQty)}
											</Typography>
										</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.year }}>
											<Typography sx={{ fontWeight: 700, color: '#7c3f00' }}>
												{numberFormatter.format(row.yearToDateValue)}
											</Typography>
										</TableCell>
										<TableCell align="right" sx={{ backgroundColor: PROCESS_TABLE_COLUMN_COLORS.year }}>
											<Typography sx={{ fontWeight: 700, color: '#184e77' }}>
												{formatCurrencyOrEmpty(getCostPerPiece(row.yearToDateValue, row.yearToDateQty))}
											</Typography>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={10} align="center" sx={{ py: 6, color: '#6b7a88' }}>
										No process records available.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			<Paper
				sx={{
					mt: 2,
					p: 2,
					borderRadius: 3,
					border: '1px solid rgba(15, 76, 117, 0.14)',
					background: 'linear-gradient(135deg, #f6fbff 0%, #ffffff 52%, #f2fff8 100%)',
					boxShadow: '0 10px 24px rgba(16, 24, 40, 0.06)',
				}}
			>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
					<Typography
						sx={{
							fontSize: { xs: 17, md: 20 },
							fontWeight: 800,
							color: '#0f3f5d',
							px: 1.2,
							py: 0.35,
							borderRadius: 1,
							background: `linear-gradient(90deg, ${alpha('#1f7a8c', 0.14)} 0%, ${alpha('#1f7a8c', 0.06)} 100%)`,
							border: `1px solid ${alpha('#1f7a8c', 0.25)}`,
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.75,
							transition: 'all 0.2s ease',
							'&::before': {
								content: '""',
								width: 8,
								height: 8,
								borderRadius: '50%',
								backgroundColor: '#1f7a8c',
								boxShadow: `0 0 0 3px ${alpha('#1f7a8c', 0.18)}`,
							},
					
						}}
					>
						Over Heads
					</Typography>
					<Chip
						size="small"
						label={`As on ${moment(toDate).format('DD MMM YYYY')}`}
						sx={{ backgroundColor: alpha('#1f7a8c', 0.12), color: '#1f5f6d', fontWeight: 700 }}
					/>
				</Stack>

				<Grid container spacing={1.5}>
					<Grid item xs={12} md={6}>
						<Card
							onClick={handleOpenExpenseBreakdownPopup}
							sx={{
								borderRadius: 2.5,
								border: '1px solid rgba(24, 78, 119, 0.16)',
								background: 'linear-gradient(135deg, rgba(24,78,119,0.12) 0%, #ffffff 72%)',
								cursor: 'pointer',
								'&:hover': { background: 'linear-gradient(135deg, rgba(24,78,119,0.18) 0%, #ffffff 72%)' },
							}}
						>
							<Stack sx={{ p: 1.5 }} spacing={0.5}>
								<Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: '#5c6b77' }}>
									Selected Month Expense ({expenseSummary.selectedMonthLabel})
								</Typography>
								<Typography sx={{ fontSize: 17, fontWeight: 900, color: '#184e77' }}>
									Rs {currencyFormatter.format(expenseSummary.selectedMonthTotal)}
								</Typography>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} md={6}>
						<Card
							onClick={handleOpenYearExpenseBreakdownPopup}
							sx={{
								borderRadius: 2.5,
								border: '1px solid rgba(33, 156, 144, 0.22)',
								background: 'linear-gradient(135deg, rgba(33,156,144,0.14) 0%, #ffffff 72%)',
								cursor: 'pointer',
								'&:hover': { background: 'linear-gradient(135deg, rgba(33,156,144,0.2) 0%, #ffffff 72%)' },
							}}
						>
							<Stack sx={{ p: 1.5 }} spacing={0.5}>
								<Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: '#5c6b77' }}>
									Selected Year Expense ({expenseSummary.selectedYear})
								</Typography>
								<Typography sx={{ fontSize: 17, fontWeight: 900, color: '#219c90' }}>
									Rs {currencyFormatter.format(expenseSummary.selectedYearTotal)}
								</Typography>
							</Stack>
						</Card>
					</Grid>
				</Grid>


			</Paper>

			<Paper
				sx={{
					mt: 2,
					borderRadius: 3,
					overflow: 'hidden',
					border: '1px solid rgba(25, 87, 122, 0.16)',
					boxShadow: '0 10px 24px rgba(16, 24, 40, 0.06)',
					background: 'linear-gradient(135deg, #f4fbff 0%, #ffffff 60%, #fffaf0 100%)',
				}}
			>
				<Box
					sx={{
						px: 2,
						py: 1.25,
						background: 'linear-gradient(90deg, rgba(31,122,140,0.14) 0%, rgba(246,189,96,0.18) 100%)',
						borderBottom: '1px solid rgba(16, 24, 40, 0.08)',
					}}
				>
					<Box>
						<Typography sx={{ fontSize: 18, fontWeight: 800, color: '#12314d' }}>
							Month Wise Overall Cost 
						</Typography>
						<Typography sx={{ color: '#5f7282', fontSize: 12 }}>
							Quantity, Value, Expense and Cost Per Piece month by month.
						</Typography>
					</Box>
				</Box>

				<TableContainer sx={{ px: 1.5, pb: 1.5 }}>
					<Table size="small" sx={{ minWidth: 1250 }}>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 800, color: '#12314d', minWidth: 140 }}>Particulars</TableCell>
								{monthwiseCostByMonth.map((row) => (
									<TableCell key={`head-${row.MonthNumber}`} align="right" sx={{ fontWeight: 800, color: '#12314d', minWidth: 86 }}>
										{row?.MonthName || moment().month(row.MonthNumber - 1).format('MMM')}
									</TableCell>
								))}
								<TableCell align="right" sx={{ fontWeight: 800, color: '#12314d', minWidth: 96 }}>Total</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{monthwiseCostByMonth.length > 0 ? (
								<>
									<TableRow hover>
										<TableCell sx={{ fontWeight: 700, color: '#184e77' }}>Prod. Qty (Pcs)</TableCell>
										{monthwiseCostByMonth.map((row) => (
											<TableCell
												key={`qty-${row.MonthNumber}`}
												align="right"
												sx={Number(row?.MonthNumber) === moment(toDate).month() + 1 ? { backgroundColor: alpha('#1f7a8c', 0.08) } : undefined}
											>
												{formatNumberOrEmpty(row?.Quantity)}
											</TableCell>
										))}
										<TableCell align="right" sx={{ fontWeight: 800, backgroundColor: '#eef8ff' }}>
											{formatNumberOrEmpty(monthwiseCostTotals.quantity)}
										</TableCell>
									</TableRow>
									<TableRow hover>
										<TableCell sx={{ fontWeight: 700, color: '#7c3f00' }}>CMP Cost</TableCell>
										{monthwiseCostByMonth.map((row) => (
											<TableCell
												key={`value-${row.MonthNumber}`}
												align="right"
												sx={Number(row?.MonthNumber) === moment(toDate).month() + 1 ? { backgroundColor: alpha('#1f7a8c', 0.08) } : undefined}
											>
												{formatCurrencyOrEmpty(row?.Value)}
											</TableCell>
										))}
										<TableCell align="right" sx={{ fontWeight: 800, backgroundColor: '#eef8ff' }}>
											{formatCurrencyOrEmpty(monthwiseCostTotals.value)}
										</TableCell>
									</TableRow>
									<TableRow hover>
										<TableCell sx={{ fontWeight: 700, color: '#0f6f78' }}>Over Heads</TableCell>
										{monthwiseCostByMonth.map((row) => (
											<TableCell
												key={`expense-${row.MonthNumber}`}
												align="right"
												sx={Number(row?.MonthNumber) === moment(toDate).month() + 1 ? { backgroundColor: alpha('#1f7a8c', 0.08), fontWeight: 700 } : undefined}
											>
												{formatCurrencyOrEmpty(row?.Expense)}
											</TableCell>
										))}
										<TableCell align="right" sx={{ fontWeight: 800, backgroundColor: '#eef8ff' }}>
											{formatCurrencyOrEmpty(monthwiseCostTotals.expense)}
										</TableCell>
									</TableRow>
									<TableRow  sx={{backgroundColor:'#f6ca51'}}>
										<TableCell sx={{ fontWeight: 700, color: '#0f6f78' }}>Total Cost</TableCell>
										{monthwiseCostByMonth.map((row) => (
											<TableCell
												key={`expense-${row.MonthNumber}`}
												align="right"
												// sx={Number(row?.MonthNumber) === moment(toDate).month() + 1 ? { backgroundColor: alpha('#1f7a8c', 0.08), fontWeight: 700 } : undefined}
												sx={{ fontWeight: 700 }}
											>
												{formatCurrencyOrEmpty(Number(row?.Expense || 0) + Number(row?.Value || 0))}
											</TableCell>
										))}
										<TableCell align="right" sx={{ fontWeight: 800, backgroundColor: '#eef8ff' }}>
											{formatCurrencyOrEmpty(monthwiseCostTotals.expense + monthwiseCostTotals.value)}
										</TableCell>
									</TableRow>
									<TableRow hover>
										<TableCell sx={{ fontWeight: 700, color: '#2d5b27' }}>Avg. CMP/ Pcs</TableCell>
										{monthwiseCostByMonth.map((row) => (
											<TableCell
												key={`cpp-${row.MonthNumber}`}
												align="right"
												sx={Number(row?.MonthNumber) === moment(toDate).month() + 1 ? { backgroundColor: alpha('#1f7a8c', 0.08) } : undefined}
											>
												{formatCurrencyOrEmpty(row?.CPP)}
											</TableCell>
										))}
										<TableCell align="right" sx={{ fontWeight: 800, backgroundColor: '#eef8ff' }}>
											{formatCurrencyOrEmpty(monthwiseCostTotals.quantity > 0 ? (monthwiseCostTotals.expense + monthwiseCostTotals.value) / monthwiseCostTotals.quantity : 0)}
										</TableCell>
									</TableRow>
								</>
							) : (
								<TableRow>
									<TableCell colSpan={14} align="center" sx={{ py: 3, color: '#6b7a88' }}>
										No monthwise production cost data available.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			<Snackbar open={Boolean(errorMessage)} autoHideDuration={4000} onClose={() => setErrorMessage('')}>
				<Alert severity="error" variant="filled" onClose={() => setErrorMessage('')}>
					{errorMessage}
				</Alert>
			</Snackbar>

			<Snackbar open={Boolean(infoMessage)} autoHideDuration={3000} onClose={() => setInfoMessage('')}>
				<Alert severity="info" variant="filled" onClose={() => setInfoMessage('')}>
					{infoMessage}
				</Alert>
			</Snackbar>

			<Dialog open={expenseBreakdownPopupOpen} onClose={() => setExpenseBreakdownPopupOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle sx={{ fontWeight: 800, color: '#12314d' }}>
					Selected Month Expense Breakdown ({expenseSummary.selectedMonthLabel} {expenseSummary.selectedYear})
				</DialogTitle>
				<DialogContent>
					<TableContainer sx={{ mt: 1, borderRadius: 2, border: '1px solid rgba(16,24,40,0.08)' }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 800 }}>Expense Type</TableCell>
									<TableCell align="right" sx={{ fontWeight: 800 }}>Amount</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{monthlyExpenseBreakdown.length > 0 ? (
									monthlyExpenseBreakdown.map((item, index) => (
										<TableRow key={`${item.CostDescription || 'Expense'}-${index}`} hover>
											<TableCell>{item.CostDescription || 'Expense'}</TableCell>
											<TableCell align="right">Rs {currencyFormatter.format(Number(item.AmountPerMonth || 0))}</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={2} align="center" sx={{ py: 3, color: '#6b7a88' }}>
											No expense breakdown available.
										</TableCell>
									</TableRow>
								)}
								{monthlyExpenseBreakdown.length > 0 && (
									<TableRow sx={{ backgroundColor: '#f1f7ff' }}>
										<TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
										<TableCell align="right" sx={{ fontWeight: 800 }}>
											Rs {currencyFormatter.format(expenseSummary.selectedMonthTotal)}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</DialogContent>
			</Dialog>

			<Dialog open={yearExpenseBreakdownPopupOpen} onClose={() => setYearExpenseBreakdownPopupOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle sx={{ fontWeight: 800, color: '#12314d' }}>
					Selected Year Expense Breakdown ({expenseSummary.selectedYear})
				</DialogTitle>
				<DialogContent>
					<TableContainer sx={{ mt: 1, borderRadius: 2, border: '1px solid rgba(16,24,40,0.08)' }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 800 }}>Month</TableCell>
									<TableCell align="right" sx={{ fontWeight: 800 }}>Total Expense</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{yearlyMonthSummary.length > 0 ? (
									yearlyMonthSummary.map((item) => (
										<TableRow key={`year-expense-month-${item.month}`} hover>
											<TableCell>{moment().month(item.month - 1).format('MMMM')}</TableCell>
											<TableCell align="right">Rs {currencyFormatter.format(item.total)}</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={2} align="center" sx={{ py: 3, color: '#6b7a88' }}>
											No year expense breakdown available.
										</TableCell>
									</TableRow>
								)}
								{yearlyMonthSummary.length > 0 && (
									<TableRow sx={{ backgroundColor: '#edfdf8' }}>
										<TableCell sx={{ fontWeight: 800 }}>Grand Total</TableCell>
										<TableCell align="right" sx={{ fontWeight: 800 }}>
											Rs {currencyFormatter.format(expenseSummary.selectedYearTotal)}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</DialogContent>
			</Dialog>
		</Container>
	);
}
